// ═══════════════════════════════════════════════
//   WINDOWS — desktop window manager.
//   Loaded after infra.js, before bootstrap.js.
//
//   Exposes (global):
//     drag(e, id)           — start dragging window by id
//     front(el)             — bring window/popup to front (z-index)
//     openWin(id)           — show window + SFX
//     closeWin(id)          — hide window + SFX
//     minimizeWin(id)       — hide + add a tab to the taskbar
//     restoreWin(id)        — show again, remove the taskbar tab
//     toggleMaxWin(id)      — fill desktop / restore prior geometry
// ═══════════════════════════════════════════════

const {S, CONFIG, SFX} = window;

// ══════════════════════════════════════════
// MOBILE DETECTION
// ══════════════════════════════════════════
// Sets `body.is-mobile` for CSS overrides (styles/mobile.css) and
// exposes `S.isMobile` for JS short-circuits. Listens to matchMedia
// so rotation / resize flips the state live. The breakpoint matches
// the @media query in styles/mobile.css (768px) — keep them in sync.
const _mobileMQ = window.matchMedia('(max-width: 768px)');
function _applyMobile() {
    const m = _mobileMQ.matches;
    document.body.classList.toggle('is-mobile', m);
    if (window.S) window.S.isMobile = m;
}
_applyMobile();
// Safari < 14 needs addListener (deprecated alias); modern browsers
// have addEventListener('change'). Use whichever exists.
if (_mobileMQ.addEventListener) _mobileMQ.addEventListener('change', _applyMobile);
else if (_mobileMQ.addListener)  _mobileMQ.addListener(_applyMobile);

function _isMobile() { return _mobileMQ.matches; }

// ══════════════════════════════════════════
// WINDOW DRAG + Z-ORDER
// ══════════════════════════════════════════
let dEl = null, dox = 0, doy = 0;

function drag(e, id) {
    // On mobile, windows are fullscreen — dragging is meaningless
    // and would conflict with native scroll/touch behaviour.
    if (_isMobile()) {
        front(document.getElementById(id));
        return;
    }
    dEl = document.getElementById(id);
    front(dEl);
    const r = dEl.getBoundingClientRect();
    dox = e.clientX - r.left;
    doy = e.clientY - r.top;
    e.preventDefault();
}

// Body uses CSS `zoom` to scale the whole desktop on bigger viewports.
// clientX/getBoundingClientRect return zoomed/visual coords, while
// offsetWidth and style.left are in layout (un-zoomed) coords — so we
// translate between the two when dragging.
function _bodyZoom() {
    return parseFloat(getComputedStyle(document.body).zoom) || 1;
}

document.addEventListener('mousemove', e => {
    if (!dEl) return;
    const z = _bodyZoom();
    let x = (e.clientX - dox) / z;
    let y = (e.clientY - doy) / z;
    x = Math.max(0, Math.min(window.innerWidth  / z - dEl.offsetWidth,  x));
    y = Math.max(0, Math.min(window.innerHeight / z - 52 - dEl.offsetHeight, y));
    dEl.style.left = x + 'px';
    dEl.style.top = y + 'px';
});
document.addEventListener('mouseup', () => dEl = null);

function front(el) {
    el.style.zIndex = ++S.zTop;
}

// ══════════════════════════════════════════
// MINIMIZE / MAXIMIZE
// ══════════════════════════════════════════
// Per-window state for the chrome buttons. Created lazily; stays around
// across open/close so a maximised window still remembers its prior
// geometry after the user closes and reopens it.
const _winState = {};
function _state(id) {
    if (!_winState[id]) _winState[id] = {minimized: false, maxed: false, geom: null};
    return _winState[id];
}

// Squeeze a wurl like "♡www.konbini-shop.bun" → "shop" so the taskbar
// chip stays narrow. Falls back to the window id (minus the "w-")
// when the URL doesn't match the project's usual pattern.
function _shortLabel(fullText, id) {
    let s = fullText.replace(/^[^a-z0-9]+/i, ''); // drop leading icon glyphs
    s = s.replace(/\.[a-z]+$/i, '');              // drop ".bun" / ".com"
    s = s.replace(/^(?:www|mail|m)\./i, '');      // drop "www." / "mail." prefix
    s = s.replace(/^konbini-/i, '');              // drop project prefix
    if (!s) s = id.replace(/^w-/, '');
    return s.slice(0, 14);
}

function _addMinTab(id) {
    const host = document.getElementById('tb-mins');
    if (!host) return;
    if (host.querySelector(`[data-win="${id}"]`)) return;
    const w = document.getElementById(id);
    if (!w) return;
    const wurl = w.querySelector('.wurl');
    const icon = wurl?.querySelector('.wurl-h')?.textContent.trim() || '♡';
    const fullText = (wurl?.textContent || id).trim();
    const short = _shortLabel(fullText, id);
    const btn = document.createElement('button');
    btn.className = 'tb-min';
    btn.setAttribute('data-win', id);
    btn.setAttribute('title', fullText);
    btn.addEventListener('click', () => restoreWin(id));
    btn.innerHTML = `<span class="tb-min-icon">${icon}</span><span class="tb-min-name">${short}</span>`;
    host.appendChild(btn);
    _updateMinsLayout();
}

function _removeMinTab(id) {
    const host = document.getElementById('tb-mins');
    if (!host) return;
    const btn = host.querySelector(`[data-win="${id}"]`);
    if (btn) btn.remove();
    _updateMinsLayout();
}

// Drop labels when the chips would otherwise overflow the taskbar
// strip — measured in two passes because removing the labels can
// take us back under the threshold and we want full labels again
// once there's room for them.
function _updateMinsLayout() {
    const host = document.getElementById('tb-mins');
    if (!host) return;
    host.classList.remove('compact');
    if (host.scrollWidth > host.clientWidth + 1) {
        host.classList.add('compact');
    }
}

window.addEventListener('resize', _updateMinsLayout);

function minimizeWin(id) {
    const w = document.getElementById(id);
    if (!w) return;
    // On mobile the .wb.y button is hidden and .tb-mins is hidden too,
    // so minimising would orphan the window state. The chrome button
    // can't trigger this on mobile, but guard defensively in case any
    // future caller invokes it programmatically.
    if (_isMobile()) return;
    const st = _state(id);
    if (st.minimized) return;
    st.minimized = true;
    w.style.display = 'none';
    _addMinTab(id);
    SFX.play('close');
}

function restoreWin(id) {
    const w = document.getElementById(id);
    if (!w) return;
    const st = _state(id);
    st.minimized = false;
    w.style.display = 'flex';
    front(w);
    _removeMinTab(id);
    SFX.play('open');
}

function toggleMaxWin(id) {
    const w = document.getElementById(id);
    if (!w) return;
    // On mobile every window is already fullscreen via CSS, so the
    // orange button is a no-op. The .wb.o button is also hidden via
    // styles/mobile.css — this guard is defensive in case anything
    // calls toggleMaxWin programmatically.
    if (_isMobile()) { front(w); return; }
    const st = _state(id);
    if (!st.maxed) {
        st.geom = {
            left:   w.style.left,
            top:    w.style.top,
            width:  w.style.width,
            height: w.style.height,
            maxHeight: w.style.maxHeight,
        };
        const z = _bodyZoom();
        const pad = 8;
        const vw = window.innerWidth  / z;
        const vh = window.innerHeight / z - 52; // taskbar height
        w.style.left   = pad + 'px';
        w.style.top    = pad + 'px';
        w.style.width  = (vw - pad * 2) + 'px';
        w.style.height = (vh - pad * 2) + 'px';
        w.style.maxHeight = '';
        w.classList.add('maxed');
        st.maxed = true;
    } else {
        const g = st.geom || {};
        w.style.left   = g.left   || '';
        w.style.top    = g.top    || '';
        w.style.width  = g.width  || '';
        w.style.height = g.height || '';
        w.style.maxHeight = g.maxHeight || '';
        w.classList.remove('maxed');
        st.maxed = false;
        _fitWinToViewport(w);
    }
    front(w);
    SFX.play('open');
}

function _fitWinToViewport(w) {
    if (w.classList.contains('maxed')) return;
    if (_isMobile()) return;
    const z = _bodyZoom();
    const vh = window.innerHeight / z;
    const taskbarH = 52;
    const margin = 8;
    const maxAvail = vh - taskbarH - margin * 2;
    if (maxAvail < 200) return;

    const naturalH = w.offsetHeight;
    let top = parseFloat(w.style.top) || w.offsetTop || 0;

    if (top + naturalH + taskbarH + margin > vh) {
        top = Math.max(margin, vh - taskbarH - margin - naturalH);
        w.style.top = top + 'px';
    }
    const available = vh - taskbarH - margin - (parseFloat(w.style.top) || 0);
    if (naturalH > available) {
        w.style.maxHeight = available + 'px';
    }
}

function openWin(id) {
    const w = document.getElementById(id);
    if (!w) return;
    const st = _state(id);
    // If the window was minimised, opening it from a desktop icon (or any
    // other entry point) should also clear the taskbar tab so we don't
    // leave a stale chip behind.
    if (st.minimized) {
        st.minimized = false;
        _removeMinTab(id);
    }
    w.style.display = 'flex';
    _fitWinToViewport(w);
    front(w);
    SFX.play('open');
    // read_bio is a "passive" mission trigger: opening the About window IS
    // the action. Wire it here so it fires no matter the entry point
    // (Start menu, missions "Go →", deep link, etc.). triggerMission is a
    // no-op when the mission isn't active or is already done.
    if (id === 'w-about' && window.triggerMission) window.triggerMission('read_bio');
}

function closeWin(id) {
    const w = document.getElementById(id);
    if (!w) return;
    const st = _state(id);
    // Closing should fully reset chrome state — a reopen starts fresh
    // (un-minimised, un-maximised, no stale taskbar tab).
    if (st.minimized) _removeMinTab(id);
    if (st.maxed) {
        const g = st.geom || {};
        w.style.left   = g.left   || '';
        w.style.top    = g.top    || '';
        w.style.width  = g.width  || '';
        w.style.height = g.height || '';
        w.classList.remove('maxed');
    }
    st.minimized = false;
    st.maxed = false;
    st.geom = null;
    w.style.maxHeight = '';
    w.style.display = 'none';
    SFX.play('close');
}

// Wire the yellow / orange chrome buttons via delegation so we don't
// have to maintain inline onclicks across every single .win in HTML.
// Red still uses inline onclick because some windows (bakery, minigame)
// run extra teardown there.
document.addEventListener('click', e => {
    const btn = e.target.closest('.wb');
    if (!btn) return;
    const win = btn.closest('.win');
    if (!win) return;
    if      (btn.classList.contains('y')) minimizeWin(win.id);
    else if (btn.classList.contains('o')) toggleMaxWin(win.id);
});

document.querySelectorAll('.win,.popup').forEach(w => w.addEventListener('mousedown', () => front(w)));


// ── expose to other classic scripts ──
window.drag         = drag;
window.front        = front;
window.openWin      = openWin;
window.closeWin     = closeWin;
window.minimizeWin  = minimizeWin;
window.restoreWin   = restoreWin;
window.toggleMaxWin = toggleMaxWin;

// ═══════════════════════════════════════════════
//   RESIZABLE WINDOWS — bottom-right resize handle
// ═══════════════════════════════════════════════

// ══════════════════════════════════════════
// RESIZABLE WINDOWS
// ══════════════════════════════════════════
function initResizable() {
    if (!window.CONFIG?.WINDOWS_RESIZABLE) return;
    // Skip on mobile — the bottom-right handle is mouse-only and the
    // windows are fullscreen anyway. styles/mobile.css also hides
    // .win-rz visually; this short-circuit avoids attaching the
    // listener at all.
    if (_isMobile()) return;
    document.querySelectorAll('.win').forEach(win => {
        if (win.hasAttribute('data-noresize')) return;
        if (win.querySelector('.win-rz')) return;
        const h = document.createElement('div');
        h.className = 'win-rz';
        win.appendChild(h);
        h.addEventListener('mousedown', e => {
            e.preventDefault();
            e.stopPropagation();
            const sx = e.clientX, sy = e.clientY;
            const sw = win.offsetWidth, sh = win.offsetHeight;
            // Collect all content children (not wbar / wfoot / resize handle)
            const contentEls = [...win.children].filter(c =>
                !c.classList.contains('wbar') && !c.classList.contains('wfoot') && !c.classList.contains('win-rz')
            );
            // Read current zoom so multiple drags compound correctly
            const curZoom = parseFloat(contentEls[0]?.style.zoom || '1') || 1;

            const ratio = sw / sh;
            const minW = 220, minH = 80;

            function onMove(e) {
                const dx = e.clientX - sx;
                const dy = e.clientY - sy;
                // Drive resize by whichever delta is larger, then derive the other from the locked aspect ratio
                let newW, newH;
                if (Math.abs(dx) >= Math.abs(dy)) {
                    newW = Math.max(minW, sw + dx);
                    newH = newW / ratio;
                } else {
                    newH = Math.max(minH, sh + dy);
                    newW = newH * ratio;
                }
                if (newH < minH) { newH = minH; newW = newH * ratio; }
                if (newW < minW) { newW = minW; newH = newW / ratio; }
                win.style.width = newW + 'px';
                win.style.height = newH + 'px';
                const newZoom = Math.max(0.6, Math.min(3, curZoom * (newW / sw)));
                contentEls.forEach(c => c.style.zoom = newZoom.toFixed(3));
            }

            function onUp() {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            }

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        });
    });
}

window.initResizable = initResizable;
